import React from 'react';
import { Tv2, Radio, Smartphone, Globe2, PlaySquare, Wifi, Zap } from 'lucide-react';

interface LandingHeroProps {
  onWatchClick: () => void;
}

export function LandingHero({ onWatchClick }: LandingHeroProps) {
  return (
    <div className="min-h-full pb-20">
      {/* Animated Background */}
      <div className="fixed inset-0 bg-gradient-hero">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-600 rounded-full mix-blend-multiply filter blur-xl animate-blob" />
          <div className="absolute top-0 -right-4 w-72 h-72 bg-red-600 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000" />
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-blue-600 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000" />
        </div>
      </div>
      
      {/* Hero Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <div className="inline-block animate-float">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-red-500 to-purple-600 rounded-lg blur opacity-30" />
              <h1 className="relative text-5xl md:text-7xl font-bold text-white mb-6">
                tv.flue<span className="text-red-500">.live</span>
              </h1>
            </div>
          </div>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
            A nova era da <span className="text-red-400 font-semibold">TV digital</span>. 
            Distribuição inteligente, alcance global e tecnologia de ponta.
          </p>
          <div className="flex flex-wrap gap-4 justify-center mb-16">
            <button
              onClick={onWatchClick}
              className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-red-500 to-purple-600 
                text-white rounded-xl transition-all duration-300 shadow-lg shadow-purple-500/25
                hover:shadow-xl hover:shadow-purple-500/40 hover:-translate-y-0.5"
            >
              <PlaySquare className="h-5 w-5 mr-2 group-hover:animate-pulse" />
              <span className="font-medium">Assistir Agora</span>
            </button>
            <a
              href="https://wa.me/5511999999999"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center px-8 py-4 bg-white/10 backdrop-blur-sm
                text-white rounded-xl transition-all duration-300 border border-white/20
                hover:bg-white/20 hover:-translate-y-0.5"
            >
              <Wifi className="h-5 w-5 mr-2 group-hover:animate-pulse" />
              <span className="font-medium">Transmita seu Canal</span>
            </a>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
            <Globe2 className="h-8 w-8 text-red-400 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Distribuição Global</h3>
            <p className="text-gray-400 text-sm">
              Alcance milhões de espectadores através de nossa rede otimizada.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
            <Smartphone className="h-8 w-8 text-purple-400 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Multi-plataforma</h3>
            <p className="text-gray-400 text-sm">
              Compatível com Smart TVs, IPTV e dispositivos móveis.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
            <Radio className="h-8 w-8 text-blue-400 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Player Profissional</h3>
            <p className="text-gray-400 text-sm">
              Transmissão em alta qualidade com player otimizado.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
            <Zap className="h-8 w-8 text-green-400 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Monetização</h3>
            <p className="text-gray-400 text-sm">
              Oportunidades de receita através de anúncios na rede.
            </p>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <div className="max-w-3xl mx-auto bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Seja parte da revolução digital
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Junte-se à nossa rede de transmissão e alcance um novo público. 
              Oferecemos suporte técnico completo, monetização e integração com múltiplas plataformas.
            </p>
            <a
              href="https://wa.me/5511999999999"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 
                text-white rounded-xl hover:opacity-90 transition-opacity duration-300 shadow-lg shadow-green-500/25"
            >
              <Wifi className="h-6 w-6 mr-2" />
              <span className="font-medium">Comece a Transmitir</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}